import { useTranslation } from "react-i18next";
import React, { useEffect } from "react";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../hooks/useTypedSelector";
import DealerCard from "../commons/DealerCard/DealerCard";
import licencePlateSelectBg from "../../../../general/fields/modules/commons/licencePlateSelectField/assets/licencePlateSelectBg.svg";
import { GripVertical } from "react-bootstrap-icons";
import { Draggable, Droppable } from "@hello-pangea/dnd";
import ReactDOM from "react-dom";
import {
  DealerRequestModel,
  RequestDealerModel,
} from "../../domain/models/dealerModel";
import { UnloadTruck } from "../commons/Dropzone/UnloadTruck";
import LicensePlate from "license-plate";
import { getRequestsFromDay } from "../../infrastructure/redux/PlanningThunks";
import { Form } from "react-bootstrap";
import { setRequestsDate } from "../../infrastructure/redux/PlanningSlice";

const portal = document.createElement("div");
document.body.appendChild(portal);

export const Requests = () => {
  const { t } = useTranslation("common");
  const dispatch = useAppDispatch();

  const { dealers, date, draggableId, requestsDate } = useAppSelector(
    (state) => state.planning,
  );

  useEffect(() => {
    if (dealers.length === 0) {
      void dispatch(getRequestsFromDay(date));
    }
  }, []);

  const draggingFromTruck =
    draggableId?.startsWith("slip:") || draggableId?.startsWith("request:");

  const draggableDealer = (dealer: DealerRequestModel, index: number) => {
    return (
      <Draggable
        key={"new-dealer:" + dealer.relation.id}
        draggableId={"new-dealer:" + dealer.relation.id}
        index={index}
      >
        {(provided, snapshot) => {
          const child = (
            <>
              <div
                ref={provided.innerRef}
                {...provided.draggableProps}
                {...provided.dragHandleProps}
              >
                <div style={{ width: "260px" }} className={"me-3"}>
                  <DealerCard
                    key={"dealer-" + dealer.relation.id}
                    className={"mt-2"}
                  >
                    <DealerCard.Header>
                      <div className={"d-flex flex-column"}>
                        <div className={"d-flex"}>
                          <h5>{dealer.relation.name}</h5>
                          {/*<CheckCircleFill className={"ms-2"} color={"green"} />*/}
                        </div>
                        <span>
                          {dealer.deliver_requests.length} {t("deliver")} |{" "}
                          {dealer.pickup_amount ?? 0} {t("pickup")}
                        </span>
                      </div>
                    </DealerCard.Header>
                    <DealerCard.Content>
                      <p>{t("Deliver")}</p>
                      <div className={"d-flex flex-wrap"}>
                        {dealer.deliver_requests.map((request, indexRequest) =>
                          draggableRequest(request, indexRequest),
                        )}
                      </div>

                      <p>{t("Pickup")}</p>
                      {dealer.pickup_amount ?? 0}
                    </DealerCard.Content>
                  </DealerCard>
                </div>
              </div>
            </>
          );

          if (!snapshot.isDragging) {
            return child;
          }

          return ReactDOM.createPortal(child, portal);
        }}
      </Draggable>
    );
  };

  const draggableRequest = (request: RequestDealerModel, index: number) => {
    return (
      <Draggable
        key={"new-request:" + request.id}
        draggableId={"new-request:" + request.id}
        index={index}
      >
        {(provided, snapshot) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const plate = new LicensePlate(request.source?.car?.licence ?? "");
          const child = (
            <div
              ref={provided.innerRef}
              {...provided.draggableProps}
              {...provided.dragHandleProps}
            >
              {plate.valid() ? (
                <div
                  className={"planning-licence-plate me-2 mt-2"}
                  key={request.id}
                  style={{
                    background: `url(${licencePlateSelectBg}) no-repeat center center / cover`,
                  }}
                >
                  <div>{plate.pretty()}</div>
                  <GripVertical
                    color={"white"}
                    width={15}
                    height={20}
                    className={"planning-licence-plate-icon"}
                  />
                </div>
              ) : (
                <div>
                  <GripVertical
                    color={"black"}
                    width={15}
                    height={20}
                    className={"planning-licence-plate-icon"}
                  />
                  {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    //@ts-ignore
                    request.source?.car?.licence ?? request.source?.car?.label
                  }
                </div>
              )}
            </div>
          );

          if (!snapshot.isDragging) {
            return child;
          }

          return ReactDOM.createPortal(child, portal);
        }}
      </Draggable>
    );
  };

  return (
    <div className={"content mt-5"}>
      <div className={"content-header mb-3"}>
        <h3>{t("Requests")}</h3>

        <Form.Group className={"ms-3"}>
          <Form.Control
            type="date"
            value={requestsDate}
            onChange={(e) => {
              dispatch(setRequestsDate(e.target.value));
              void dispatch(getRequestsFromDay(e.target.value));
            }}
          />
        </Form.Group>
      </div>
      <Droppable
        key={"remove-items"}
        droppableId={"remove-items"}
        direction={"horizontal"}
        isDropDisabled={!draggingFromTruck}
      >
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            style={{ minHeight: "200px" }}
          >
            <div
              className={"text-center"}
              style={{
                display: draggingFromTruck ? "block" : "none",
                opacity: snapshot.isDraggingOver ? 1 : 0.5,
              }}
            >
              <UnloadTruck />
            </div>

            <div
              style={{
                display: draggingFromTruck ? "none" : "block",
              }}
            >
              <div className={"d-flex flex-wrap align-items-start"}>
                {dealers.map((dealer, index) => draggableDealer(dealer, index))}
                {provided.placeholder}
              </div>
            </div>
          </div>
        )}
      </Droppable>
    </div>
  );
};
